import {
  ProjectGridColumnDef,
  ProjectGridFilter,
  ProjectGridSort,
  ProjectGridView,
} from '../models/project-grid.types';

/**
 * What the user can DO to the grid, as pure transformations of the saved view.
 *
 * Every one of these returns a NEW view rather than mutating: the view is an input
 * to an OnPush component, and an in-place edit would leave the grid showing the old
 * model with no way to tell. It also makes the whole feature set assertable without
 * a DOM — these rules are small, and small rules that are wrong (a sort that
 * silently drops the previous column, a reorder that walks off the end of the
 * array) look exactly like working ones from the outside.
 *
 * Grouping depth is capped server-side too; the cap here is the UI refusing to
 * offer a third level, not a second source of truth.
 */

/** Deeper grouping complicates the row model and the header for a case nobody asked for. */
export const MAX_GROUP_LEVELS = 2;

/** The column that cannot be hidden. A grid of anonymous rows is unusable. */
export const REQUIRED_COLUMN = 'ProjectName';

/**
 * Click a header to sort by it.
 *
 * FIRST CLICK ASCENDING, second descending, third clears — with one exception:
 * a date column starts DESCENDING, because "most recent first" is what anyone
 * clicking a date column means, and making them click twice for it is a small
 * insult repeated every session.
 *
 * `additive` (shift-click) appends instead of replacing, so a second column
 * refines the first rather than discarding it. Without it, multi-column sort is
 * only reachable by saving a view by hand.
 */
export function toggleSort(
  view: ProjectGridView,
  columnId: string,
  column: ProjectGridColumnDef | undefined,
  additive = false
): ProjectGridView {
  const existing = view.sort.find((s) => s.columnId === columnId);
  const firstDirection: ProjectGridSort['direction'] = column?.type === 'date' ? 'desc' : 'asc';

  let next: ProjectGridSort[];
  if (!existing) {
    const added: ProjectGridSort = { columnId, direction: firstDirection };
    next = additive ? [...view.sort, added] : [added];
  } else if (existing.direction === firstDirection) {
    const flipped: ProjectGridSort = {
      columnId,
      direction: firstDirection === 'asc' ? 'desc' : 'asc',
    };
    next = additive
      ? view.sort.map((s) => (s.columnId === columnId ? flipped : s))
      : [flipped];
  } else {
    // Third click clears THIS column. In additive mode the others survive — the
    // point of shift-clicking is that the columns are independent.
    next = view.sort.filter((s) => s.columnId !== columnId);
  }

  return { ...view, sort: next };
}

/** Which sort position and direction a header should show, or null when unsorted. */
export function sortIndicator(
  view: ProjectGridView,
  columnId: string
): { direction: 'asc' | 'desc'; index: number } | null {
  const index = view.sort.findIndex((s) => s.columnId === columnId);
  return index < 0 ? null : { direction: view.sort[index].direction, index };
}

/**
 * Show or hide a column.
 *
 * A newly shown column goes to the END rather than to a remembered position:
 * remembering would mean carrying a second, invisible ordering that the one
 * `columns` array exists to avoid. Hiding the required column is refused outright
 * rather than silently ignored — the caller should not have to know.
 */
export function toggleColumn(view: ProjectGridView, columnId: string): ProjectGridView {
  if (columnId === REQUIRED_COLUMN) return view;

  const visible = view.columns.includes(columnId);
  const columns = visible
    ? view.columns.filter((id) => id !== columnId)
    : [...view.columns, columnId];

  return pruneReferences({ ...view, columns });
}

/**
 * Move a column left or right.
 *
 * Clamped at both ends rather than wrapping: a column that jumps from first to
 * last because someone pressed the arrow once too often is a surprise, and the
 * disabled arrow at the end of the list is the honest affordance.
 */
export function moveColumn(view: ProjectGridView, columnId: string, delta: number): ProjectGridView {
  const from = view.columns.indexOf(columnId);
  if (from < 0) return view;

  const to = Math.min(Math.max(from + delta, 0), view.columns.length - 1);
  if (to === from) return view;

  const columns = [...view.columns];
  columns.splice(to, 0, ...columns.splice(from, 1));
  return { ...view, columns };
}

/** Reorder to an explicit arrangement — the drag-and-drop entry point. */
export function reorderColumns(view: ProjectGridView, columnIds: readonly string[]): ProjectGridView {
  const known = new Set(view.columns);
  const reordered = columnIds.filter((id) => known.has(id));
  // Anything the caller forgot keeps its place at the end rather than vanishing:
  // losing a column to a dropped drag is a data-loss bug in a preference.
  const missing = view.columns.filter((id) => !reordered.includes(id));
  return { ...view, columns: [...reordered, ...missing] };
}

/**
 * Group by a column, or clear a level.
 *
 * Refuses a column that is not visible, and refuses a third level. Both are cases
 * the server would strip during normalisation anyway — doing it here means the UI
 * never offers a choice that silently evaporates on save.
 */
export function toggleGroupBy(view: ProjectGridView, columnId: string): ProjectGridView {
  if (view.groupBy.includes(columnId)) {
    return { ...view, groupBy: view.groupBy.filter((id) => id !== columnId) };
  }
  if (!view.columns.includes(columnId)) return view;
  if (view.groupBy.length >= MAX_GROUP_LEVELS) return view;

  return { ...view, groupBy: [...view.groupBy, columnId] };
}

/** Set or clear one column's filter. A null filter, or an empty one, clears it. */
export function setFilter(
  view: ProjectGridView,
  columnId: string,
  filter: ProjectGridFilter | null
): ProjectGridView {
  const filters = { ...view.filters };

  const empty =
    !filter ||
    (filter.op === 'contains' && !filter.value?.trim()) ||
    (filter.op === 'equals' && !filter.value?.trim()) ||
    (filter.op === 'in' && (filter.values ?? []).length === 0) ||
    (filter.op === 'between' && !filter.from && !filter.to);

  if (empty) {
    delete filters[columnId];
  } else {
    filters[columnId] = filter!;
  }

  return { ...view, filters };
}

/** Clear every per-column filter, leaving columns, sort and grouping alone. */
export function clearFilters(view: ProjectGridView): ProjectGridView {
  return { ...view, filters: {} };
}

/**
 * Drop sort, grouping and filters that reference a column no longer visible.
 *
 * Runs on every hide. The server normalises the same way when the view is saved,
 * but doing it here too is what stops the grid, between now and that save, from
 * sorting by a column the user just hid and cannot see to clear.
 */
function pruneReferences(view: ProjectGridView): ProjectGridView {
  const visible = new Set(view.columns);
  const filters: Record<string, ProjectGridFilter> = {};
  for (const [id, filter] of Object.entries(view.filters)) {
    if (visible.has(id)) filters[id] = filter;
  }
  return {
    ...view,
    sort: view.sort.filter((s) => visible.has(s.columnId)),
    groupBy: view.groupBy.filter((id) => visible.has(id)),
    filters,
  };
}
